/* eslint react/forbid-prop-types: 0 */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { compose } from 'redux';
import Head from '../components/head';
import SecondaryWrapper from '../components/root/secondary_wrapper';
import Module from '../components/module/module';
import AdDataComment from '../components/ad_data_comment';
import ErrorComponent from '../components/error';
import commonPropTypes from './commonPropTypes';
import withInitialScrollTracking from '../components/hocs/withInitialScrollTracking';
import { fetchFoundryResponse } from '../actions/foundry_response';
import { recordSearchTerm } from '../actions/api';
import { setShouldShowGeolocationBanner } from '../actions/i18n';
import { currentBreakpoint, onHomepage } from '../utils/dom';
import { initAdSlots } from '../utils/ads/webAdLib';
import { getReferrer } from '../utils/documentReferrer';
import ensureAllFunctionsAreBound from '../utils/ensureAllFunctionsAreBound';
import showModule from '../validators/module/showModule';
import classNames from 'classnames';
import {isFinite, get, keys, omit, reduce, pick} from 'lodash';
import DashPass from '../components/dash_pass';
import MediaQuery from '../components/common/media_query';

/*
 * TODO: Most of the properties in this `defaults` object are actually
 * not used anywhere. They should be eliminated.
 */
const defaults = {
  childContextTypes: {},
  contentAboveModules: () => null,
  contentBelowModules: () => null,
  defaultProps: {},
  displayName: 'AppContainer',
  mapDispatchToProps: {},
  mapStateToProps: (state) => [state],
  propTypes: {},
  shouldDisplayError() {
    if (!get(this.props, 'api.hasErrors')) {
      return false;
    }
    /*
     * We don't want to show an error page if the error is
     * from clicking the More Stories/Products button.
     */
    return get(this.props, 'api.fetchStatusBeforeError') !== 'moreContent';
  },
  shouldDisplayOutOfPageAd() {
    if (get(this.props, 'adsState.ad_data.ad_disable_interstitial')) {
      return false;
    }

    if (this.props.api.isInitialPageview) {
      const referrer = getReferrer(this.props.location);
      return !referrer.includes('google') && !referrer.includes('facebook');
    }

    return true;
  },
  shouldDisplayAdhesionAd() {
    const { edition_data, location } = this.props;
    return (
      !get(this.props, 'adsState.ad_data.ad_disable_adhesion_on_mobile') &&
      !onHomepage(location, edition_data)
    );
  },
  shouldDisplayHeaderAd: () => true,
  shouldDisplayInvertedStyles: () => false,
  shouldDisplayCondensedModules: () => false,
  shouldDisplayCondensedNav: () => false
};

const mapStateToProps = ([state, pipedState = {}]) => ({
  ...pipedState,
  adsState: state.ads,
  alternate_urls: state.i18n.alternate_urls,
  analytics: state.analytics,
  api: state.api,
  channels: state.analytics.channels,
  cookies: state.cookies,
  experiments: state.experiments,
  footerLinks: state.i18n.footerLinks,
  geolocation: state.i18n.geolocation,
  shouldShowGeolocationBanner: state.i18n.shouldShowGeolocationBanner,
  guessedBreakpoint: state.ui.guessedBreakpoint,
  isPreviewPage: state.isPreviewPage,
  locale: state.i18n.locale,
  messages: state.i18n.messages,
  meta_data: state.meta,
  modules: state.modules,
  navigationItems: state.navigation,
  trendingItems: state.trendingItems,
  newsletters: state.i18n.newsletters,
  productList: state.productList,
  schema: state.i18n.schema,
  edition_data: state.i18n.edition_data,
  wordpressApiHost: state.wordpressApiHost
});

export const defaultChildContextTypes = {
  channels: PropTypes.arrayOf(PropTypes.string),
  cookies: PropTypes.object,
  experiments: PropTypes.object,
  guessedBreakpoint: PropTypes.string,
  isPreviewPage: PropTypes.bool,
  location: PropTypes.object,
  edition_data: PropTypes.object,
  wordpressApiHost: PropTypes.string
};

export default ({
  childContextTypes,
  contextTypes,
  defaultProps,
  displayName,
  propTypes,
  mapDispatchToProps,
  shouldDecorateContainer = true,
  ...options
}) => {
  class AppContainer extends Component {
    static displayName = displayName;

    static propTypes = {
      // commonPropTypes.js
      ...commonPropTypes,

      /*
       * Delegated
       */
      ...propTypes
    };

    static childContextTypes = {
      ...defaultChildContextTypes,
      ...childContextTypes
    };

    static contextTypes = contextTypes;

    static defaultProps = {
      ...defaultProps
    };

    constructor(props, context) {
      super(props, context);
      this.state = {};
      Object.assign(
        this,
        ensureAllFunctionsAreBound(
          omit(defaults, [
            'childContextTypes',
            'defaultProps',
            'displayName',
            'mapDispatchToProps',
            'mapStateToProps',
            'propTypes',
            'shouldDecorateContainer'
          ]),
          this
        ),
        ensureAllFunctionsAreBound(
          omit(options, [
            'componentDidMount',
            'componentDidUpdate',
            'mapStateToProps'
          ]),
          this
        )
      );
    }

    getChildContext = () =>
      pick(this.props, keys(this.constructor.childContextTypes));

    componentDidMount() {
      const adToMove = document.querySelectorAll("[data-ad-position='top']");
      const originalContainer =
        adToMove.length > 0 ? adToMove[0]?.parentNode : null;
      if (originalContainer) {
        let targetContainer =
          document.querySelectorAll("[class='cta-bar']")[0];
        if (currentBreakpoint() !== 'desktop') {
          targetContainer = document.querySelectorAll("[class='hero-row']")[0];
        }
        if (targetContainer) {
          targetContainer.appendChild(originalContainer);
        }
      }
      if (options.componentDidMount) {
        options.componentDidMount.call(this);
      }
    }

    /*
     * This top-level `shouldComponentUpdate` is very important, for a reason
     * beyond performance optimization.
     *
     * Check out ROUTING.MD for an explanation.
     */
    shouldComponentUpdate(nextProps) {
      if (
        ['fetchingNewPage', 'fetchingOnSamePage'].includes(
          nextProps.api.fetchStatus
        ) &&
        nextProps.api.fetchStatus === this.props.api.fetchStatus
      ) {
        return false;
      }
      return true;
    }

    componentDidUpdate(...args) {
      if (
        args[0].api.fetchStatus === 'fetchingNewPage' &&
        this.props.api.fetchStatus === 'notFetching' &&
        !this.props.api.isInitialPageview
      ) {
        initAdSlots();
      }

      if (options.componentDidUpdate) {
        options.componentDidUpdate.call(this, ...args);
      }
    }

    modules = () => (
      <div id="below-the-fold-modules">
        {reduce(
          this.props.modules,
          (acc, module, index) => {
            if (showModule(module.type, this.props.analytics.page_type)) {
              acc.push(
                <Module
                  {...module}
                  key={index}
                  id={index + 1}
                  page_type={this.props.analytics.page_type}
                  adsState={this.props.adsState}
                  archivesState={this.state.archives}
                />
              );
            }
            return acc;
          },
          []
        )}
      </div>
    );

    secondaryWrapper() {
      const secondaryWrapperProps = {
        ...pick(this.props, [
          'adsState',
          'alternate_urls',
          'analytics',
          'api',
          'footerLinks',
          'location',
          'meta_data',
          'modules',
          'navigationItems',
          'newsletters',
          'productList',
          'recordSearchTerm',
          'setShouldShowGeolocationBanner',
          'edition_data',
          'geolocation',
          'shouldShowGeolocationBanner',
          'trendingItems',
          'experiments'
        ]),
        containerMethods: pick(this, [
          'shouldDisplayInvertedStyles',
          'shouldDisplayCondensedModules',
          'shouldDisplayOutOfPageAd',
          'shouldDisplayAdhesionAd',
          'shouldDisplayHeaderAd',
          'contentAboveModules',
          'modules',
          'contentBelowModules'
        ])
      };

      return <SecondaryWrapper {...secondaryWrapperProps} />;
    }

    render() {
      const { archives } = this.state;
      const {
        locale,
        messages,
        analytics,
        edition_data,
        meta_data,
        schema,
        api,
        location,
        geolocation,
        modules,
        adsState,
        alternate_urls,
        cookies
      } = this.props;

      const initialNow = isFinite(location.query.initialNow)
        ? location.query.initialNow
        : Date.now();

      const intlProviderProps = { locale, messages, initialNow };
      const headProps = {
        ad_data: adsState.ad_data,
        alternate_urls,
        analytics,
        api,
        archives,
        article: this.props.article,
        geolocation,
        meta_data,
        modules,
        product: this.props.product,
        productList: this.props.productList,
        schema,
        edition_data
      };

      /*
       * The Primary Wrapper is the div that wraps the Secondary Wrapper.
       *
       * TODO: It would be terrific to also create a PrimaryWrapper component and extract
       * some more of the presentational logic here into `js/components/root/primary_wrapper`.
       */
      const primaryWrapperClassName = classNames({
        'no-content': this.shouldDisplayError()
      });

      const dashPass = cookies.R29_UID && (
        <MediaQuery breakpoint="desktop">
          <DashPass
            pageId={analytics.object_id}
            pageType={analytics.page_type}
          />
        </MediaQuery>
      );

      const error = this.shouldDisplayError() && (
        <ErrorComponent is500Error={api.status >= 500} />
      );

      return (
        <IntlProvider {...intlProviderProps}>
          <main>
            <a id="skip" href="#r29-container" tab-index="1">
              Skip navigation!
            </a>
            <Head {...headProps} />
            <div className={primaryWrapperClassName}>
              {this.secondaryWrapper()}
              {dashPass}
              <AdDataComment ad_data={adsState.ad_data || {}} />
            </div>
            {error}
          </main>
        </IntlProvider>
      );
    }
  }

  /* For unit tests */
  if (!shouldDecorateContainer) {
    return AppContainer;
  }

  const composedMapStateToProps = compose(
    mapStateToProps,
    options.mapStateToProps || defaults.mapStateToProps
  );

  // Reference: https://react-redux.js.org/api/connect#object-shorthand-form
  const actionCreators = {
    fetchFoundryResponse,
    recordSearchTerm,
    setShouldShowGeolocationBanner,
    ...mapDispatchToProps
  };

  const decorate = compose(
    withInitialScrollTracking(),
    connect(composedMapStateToProps, actionCreators)
  );

  return decorate(AppContainer);
};
